import type { SmartLinkType } from './getSmartLinks.ts';

const UNKNOWN = 'unknown';

function sanitizeUrl(url: string): string {
	try {
		const urlObject = new URL(url);
		const hostname = urlObject.hostname;
		const pathname = urlObject.pathname;
		const firstPathSegment = pathname.split('/')[1];
		return firstPathSegment ? `${hostname}/${firstPathSegment}` : hostname;
	} catch {
		return '';
	}
}

export function getLinkType(link: string): string {
	if (
		link.includes('/jira/') ||
		link.includes('/servicedesk/') ||
		link.includes('/helpcenter/') ||
		link.includes('/projects/')
	) {
		return 'jira';
	} else if (link.includes('/wiki/')) {
		return 'confluence';
	} else if (link.includes('bitbucket')) {
		return 'bitbucket';
	} else if (link.includes('atlassian') || link.includes('jira-dev')) {
		// eslint-disable-next-line no-console
		console.log(`Resolving other atlassian link type: ${sanitizeUrl(link)}`);
		return 'otherAtlassian';
	}
	return 'thirdParty';
}

export function groupSmartLinksByHostname(smartLinks: SmartLinkType[]): SmartLinkType[][] {
	return Object.values(
		smartLinks.reduce(
			(acc, smartLink) => {
				try {
					const hostname = new URL(smartLink.resourceUrl).hostname;
					(acc[hostname] ||= []).push(smartLink);
					// eslint-disable-next-line no-unused-vars
				} catch (e) {
					(acc[UNKNOWN] ||= []).push(smartLink);
				}
				return acc;
			},
			{} as Record<string, SmartLinkType[]>,
		),
	);
}

export function splitLinksIntoBatches(
	smartLinksGroups: SmartLinkType[][],
	max: number,
): SmartLinkType[][] {
	const result: SmartLinkType[][] = [];
	for (const smartLinks of smartLinksGroups) {
		if (smartLinks.length <= max) {
			result.push(smartLinks);
		} else {
			for (let i = 0; i < smartLinks.length; i += max) {
				result.push(smartLinks.slice(i, i + max));
			}
		}
	}
	return result;
}

export function mergeSmallThirdPartyBatches(
	smartLinksGroups: SmartLinkType[][],
	min: number,
): SmartLinkType[][] {
	const result: SmartLinkType[][] = [];
	const smallThirdPartyBatches: SmartLinkType[] = [];
	for (const smartLinks of smartLinksGroups) {
		if (smartLinks.length >= min || getLinkType(smartLinks[0].resourceUrl) !== 'thirdParty') {
			result.push(smartLinks);
		} else {
			smallThirdPartyBatches.push(...smartLinks);
		}
	}
	if (smallThirdPartyBatches.length > 0) {
		result.push(smallThirdPartyBatches);
	}
	return result;
}

export type SmartLinksSuspenseBoundaryName =
	| 'content-smartlinks-first-party'
	| 'content-smartlinks-third-party';

/*
 * Helper method to determine type of link for named suspense boundary
 */
export function isFirstPartyLink(resourceUrl: string): boolean {
	const type = getLinkType(resourceUrl);

	switch (type) {
		case 'jira':
		case 'confluence':
		case 'bitbucket':
		case 'otherAtlassian':
			return true;
		default:
			return false; // treat all others as third-party, mirroring getLinkType() fn
	}
}

export function getSmartLinksBoundaryNameForUrl(url: string): SmartLinksSuspenseBoundaryName {
	return isFirstPartyLink(url)
		? 'content-smartlinks-first-party'
		: 'content-smartlinks-third-party';
}
